<template>
  <div v-if="hasItems">
    <div class="d-flex flex-row justify-content-between align-items-center mt-4 mb-2">
      <h3 class="m-0">{{ $t('Legacy Documents & Certificates') }}</h3>
      <b-button class="delete-button" @click="onDeleteClick">{{ $t('Delete legacy files') }}</b-button>
    </div>

    <b-card class="legacy-files-card shadow">
      <modal
        click-to-close
        name="delete-legacy-files-dialog"
        classes="modalBoon"
        transition="nice-modal-fade"
        :delay="100"
        :width="450"
      >
        <h2>{{ $t('Delete legacy files') }}</h2>
        <p>{{ $t('All legacy files will be deleted. Are you sure you want to delete all legacy files?') }}</p>
        <p>{{ $t('This action cannot be undone!') }}</p>
        <ul class="modalButtons">
          <li>
            <button class="darkButton" @click="onConfirmDeleteLegacyFiles">{{ $t('Delete') }}</button>
          </li>
          <li>
            <button class="lightButton" @click="onCancelDeleteLegacyFiles">{{ $t('Cancel') }}</button>
          </li>
        </ul>
      </modal>

      <b-table hover striped :items="items" :fields="fields" class="legacy-files-table mb-0">
        <template #table-colgroup>
          <col :style="{ width: '29%' }" />
          <col :style="{ width: '47%' }" />
          <col :style="{ width: '16%' }" />
        </template>

        <template #cell(fileName)="{ item }">
          <s3-file-download-button :s3Key="item.s3Key" class="mr-4" />
          <span class="font-weight-bold">{{ $t(item.fileName.split('.')[0]) }}</span>
        </template>

        <template #cell(approve)="{ item }">
          {{ [item.approvingPerson, item.approvingDate].filter(Boolean).join(', ') }}
        </template>

        <template #cell(status)="{ item }">
          <div :class="['btn', 'file-status', statusClasses[item.status] || statusClasses[fileStatuses.NOT_LOADED]]">
            {{ statusTitles[item.status] || $t('Unknown') }}
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';

import constants from '@/constants';

import StatusControl from '@/components/organisation/EditCompany/StatusControl';
import S3FileDownloadButton from '@/components/common/S3FileDownloadButton';

import { deleteLegacyFiles } from '@/store/companyFiles/actionTypes';
import { byCompanyId as getCompanyFilesByCompanyId } from '@/store/companyFiles/getterTypes';
import { mapCompanyFilesActions, mapCompanyFilesGetters } from '@/store/companyFiles/namespace';

export default {
  name: 'CompanyFilesLegacy',

  components: { StatusControl, S3FileDownloadButton },

  props: {
    company: {
      type: Object,
      required: true,
    },
  },

  data() {
    const fileStatuses = constants.companyFiles.statuses;
    return {
      fields: [
        { key: 'fileName', class: 'cell-file-name' },
        { key: 'approve', class: 'cell-approve' },
        { key: 'status', class: 'cell-status' },
      ],
      fileStatuses,
      statusTitles: {
        [fileStatuses.NOT_LOADED]: this.$t(`fileStatus.${fileStatuses.NOT_LOADED}`),
        [fileStatuses.FOR_APPROVAL]: this.$t(`fileStatus.${fileStatuses.FOR_APPROVAL}`),
        [fileStatuses.APPROVED]: this.$t(`fileStatus.${fileStatuses.APPROVED}`),
        [fileStatuses.WRONG]: this.$t(`fileStatus.${fileStatuses.WRONG}`),
      },
      statusClasses: {
        [fileStatuses.NOT_LOADED]: 'file-status-not-loaded',
        [fileStatuses.FOR_APPROVAL]: 'file-status-for-approval',
        [fileStatuses.APPROVED]: 'file-status-approved',
        [fileStatuses.WRONG]: 'file-status-wrong',
      },
    };
  },

  computed: {
    ...mapCompanyFilesGetters([getCompanyFilesByCompanyId]),

    filesDoc() {
      return this.company ? this[getCompanyFilesByCompanyId](this.company._id) : null;
    },

    legacyFiles() {
      return (this.filesDoc && this.filesDoc.filesLinks) || [];
    },

    items() {
      const files = this.legacyFiles.filter(({ status }) => status !== this.fileStatuses.NOT_LOADED);

      return files.map((file) => ({
        ...file,
        approvingDate:
          file.approvingDate == null ? null : moment(file.approvingDate).format(constants.formats.dateShort),
      }));
    },

    hasItems() {
      return this.legacyFiles.length > 0;
    },
  },

  methods: {
    ...mapCompanyFilesActions([deleteLegacyFiles]),

    onDeleteClick() {
      this.openDeleteDialog();
    },

    openDeleteDialog() {
      this.$modal.show('delete-legacy-files-dialog');
    },

    closeDeleteDialog() {
      this.$modal.hide('delete-legacy-files-dialog');
    },

    async onConfirmDeleteLegacyFiles() {
      this[deleteLegacyFiles]({ companyId: this.company._id });
    },

    onCancelDeleteLegacyFiles() {
      this.closeDeleteDialog();
    },
  },
};
</script>

<style scoped>
.legacy-files-card >>> .card-body {
  padding: 0;
}

.legacy-files-card >>> .card-body > h4 {
  padding: 0 15px;
}

.legacy-files-table >>> .cell-status {
  padding-right: 2rem;
}

.legacy-files-table >>> .cell-status .file-status {
  border-radius: 1.25rem;
  box-shadow: 0px 3px 6px 0px #97979752;
  min-width: 100%;
  border: none;
  padding-left: 2em;
  padding-right: 2em;
  white-space: nowrap;
  cursor: auto;
}

.legacy-files-table >>> tbody {
  border-top: 1px !important;
}

.legacy-files-table >>> thead {
  display: none;
}

.legacy-files-table >>> tbody > tr > td {
  vertical-align: middle;
}

.delete-button {
  border-radius: 1.25rem;
  box-shadow: 0px 3px 6px 0px #97979752;
  border: none;
  padding-left: 2em;
  padding-right: 2em;
  white-space: nowrap;
  background-color: #f0504a;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/EditCompany/CompanyFilesLegacy.vue